class HflCollectionShowcaseV4 extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    this.signal = this.abortController.signal;
    this.tabs = Array.from(this.querySelectorAll('[data-hfl-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-hfl-panel]'));
    this.collectionLink = this.querySelector('[data-hfl-collection-link]');

    this.setupEditorialVideo();

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.activateTab(index), { signal: this.signal });
      tab.addEventListener('keydown', (event) => this.handleTabKeydown(event, index), { signal: this.signal });
    });

    this.panels.forEach((panel) => this.setupPanel(panel));
    this.setupVariantPickers();
    this.setupProductForms();
    if (this.hasAttribute('data-mockup')) {
      this.addEventListener('click', (event) => this.handleMockupAdd(event), { capture: true, signal: this.signal });
    }

    const activeIndex = Math.max(0, this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));
    if (this.tabs.length) this.activateTab(activeIndex, false);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  setupEditorialVideo() {
    const video = this.querySelector('.hfl-showcase-v4__editorial-video');
    if (!video || video.closest('hfl-video')) return;

    // iOS only permits inline autoplay when the media is explicitly silent.
    // Re-applying the properties in JavaScript also covers Shopify-hosted videos
    // whose generated markup may be parsed before their muted state is applied.
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    const attemptPlayback = () => {
      if (!video.paused) return;
      const playback = video.play();
      if (playback && typeof playback.catch === 'function') playback.catch(() => {});
    };

    if (video.readyState >= 2) {
      attemptPlayback();
    } else {
      video.addEventListener('canplay', attemptPlayback, { once: true, signal: this.signal });
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') attemptPlayback();
    }, { signal: this.signal });

    // Low Power Mode can reject autoplay even for muted video. The first page
    // interaction provides a user gesture so playback can resume without a
    // separate play button in the showcase.
    document.addEventListener('touchstart', attemptPlayback, {
      once: true,
      passive: true,
      signal: this.signal,
    });
    document.addEventListener('pointerdown', attemptPlayback, {
      once: true,
      passive: true,
      signal: this.signal,
    });
  }

  activateTab(index, focus = true) {
    const tab = this.tabs[index];
    const panel = this.panels[index];
    if (!tab || !panel) return;

    this.tabs.forEach((item, itemIndex) => {
      const isActive = itemIndex === index;
      item.setAttribute('aria-selected', String(isActive));
      item.tabIndex = isActive ? 0 : -1;
    });

    this.panels.forEach((item, itemIndex) => {
      item.hidden = itemIndex !== index;
    });

    if (this.collectionLink) {
      const collectionUrl = panel.dataset.collectionUrl || '#';
      const hasCollection = collectionUrl !== '#';
      this.collectionLink.hidden = !hasCollection;
      this.collectionLink.href = hasCollection ? collectionUrl : '#';
      const label = this.collectionLink.querySelector('[data-hfl-collection-link-label]');
      if (label) label.textContent = panel.dataset.collectionLabel || 'View collection';
    }

    if (focus) tab.focus({ preventScroll: true });
  }

  handleTabKeydown(event, index) {
    let nextIndex = index;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.tabs.length - 1;
    if (nextIndex === index) return;

    event.preventDefault();
    this.activateTab(nextIndex);
  }

  setupPanel(panel) {
    const rail = panel.querySelector('[data-hfl-rail]');
    if (!rail) return;

    let pointerId;
    let startX = 0;
    let startScroll = 0;
    let hasDragged = false;

    rail.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button, form')) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = rail.scrollLeft;
      hasDragged = false;
      rail.classList.add('is-dragging');
      rail.setPointerCapture(pointerId);
    }, { signal: this.signal });

    rail.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      const distance = event.clientX - startX;
      if (Math.abs(distance) > 5) hasDragged = true;
      rail.scrollLeft = startScroll - distance;
    }, { signal: this.signal });

    const endDrag = (event) => {
      if (event.pointerId !== pointerId) return;
      if (rail.hasPointerCapture(pointerId)) rail.releasePointerCapture(pointerId);
      pointerId = undefined;
      rail.classList.remove('is-dragging');
    };

    rail.addEventListener('pointerup', endDrag, { signal: this.signal });
    rail.addEventListener('pointercancel', endDrag, { signal: this.signal });
    rail.addEventListener('click', (event) => {
      if (!hasDragged) return;
      event.preventDefault();
      event.stopPropagation();
      hasDragged = false;
    }, { capture: true, signal: this.signal });

  }

  setupVariantPickers() {
    this.querySelectorAll('[data-hfl-variant]').forEach((button) => {
      button.addEventListener('click', () => {
        const form = button.closest('form');
        const input = form?.querySelector('input[name="id"]');
        if (!form || !input) return;

        input.value = button.dataset.variantId;
        const quantity = button.dataset.quantity || '1';
        const quantityInput = form.querySelector('input[name="quantity"]');
        if (quantityInput) quantityInput.value = quantity;

        const component = form.closest('product-form-component');
        if (component) component.dataset.quantityDefault = quantity;
      }, { signal: this.signal });
    });
  }

  setupProductForms() {
    this.querySelectorAll('[data-hfl-quickshop-form]').forEach((form) => {
      const purchaseButtons = Array.from(form.querySelectorAll('[data-hfl-purchase]'));
      if (!purchaseButtons.length) return;

      let activeButton;
      let activeLabel;
      let defaultLabel;
      let resetTimer;

      const begin = (event) => {
        activeButton = event.submitter?.closest('[data-hfl-purchase]');
        if (!activeButton) return;
        activeLabel = activeButton.querySelector('[data-hfl-purchase-label]');
        defaultLabel = activeLabel?.textContent || '';
        purchaseButtons.forEach((button) => { button.disabled = true; });
        if (activeLabel) activeLabel.textContent = 'Adding…';
      };

      const finish = (message) => {
        window.clearTimeout(resetTimer);
        if (activeLabel) activeLabel.textContent = message;
        resetTimer = window.setTimeout(() => {
          purchaseButtons.forEach((button) => { button.disabled = false; });
          if (activeLabel) activeLabel.textContent = defaultLabel;
          activeButton = undefined;
          activeLabel = undefined;
        }, 1600);
      };

      if (!this.hasAttribute('data-mockup')) {
        form.addEventListener('submit', begin, { signal: this.signal });
      }

      const component = form.closest('product-form-component');
      component?.addEventListener('cart:update', () => finish('Added'), { signal: this.signal });
      component?.addEventListener('cart:error', () => finish('Try again'), { signal: this.signal });
    });
  }

  handleMockupAdd(event) {
    const purchaseButton = event.target.closest('[data-hfl-purchase]');
    if (!purchaseButton || !this.contains(purchaseButton)) return;

    event.preventDefault();
    const form = purchaseButton.closest('form');
    const variantInput = form?.querySelector('input[name="id"]');
    if (purchaseButton.dataset.variantId && variantInput) {
      variantInput.value = purchaseButton.dataset.variantId;
    }

    const label = purchaseButton.querySelector('[data-hfl-purchase-label]');
    const defaultLabel = label?.textContent || '';
    purchaseButton.dataset.mockupAdded = 'true';
    purchaseButton.disabled = true;
    if (label) label.textContent = 'Adding…';

    window.setTimeout(() => {
      purchaseButton.disabled = false;
      if (label) label.textContent = 'Added';
      window.setTimeout(() => {
        if (label) label.textContent = defaultLabel;
      }, 1600);
    }, 260);
  }
}

if (!customElements.get('hfl-collection-showcase-v4')) {
  customElements.define('hfl-collection-showcase-v4', HflCollectionShowcaseV4);
}
